/* eslint-disable no-console */
import type { AxiosResponse } from 'axios';

import { useCallback } from 'react';

import type { MutationFunction, UseMutationOptions } from 'react-query';

import { useMutation } from 'react-query';

import type { ErrorType } from 'state/custom-instance';

import { customInstance } from 'state/custom-instance';

import client from 'state/remote/client';

import useGetDocumentTypes from './use-get-document-types';

export interface PersonRegisterUploadType {
  id?: number;
  freelancer?: number;
  dtype: number;
  picture?: File | null;
  pdf?: File | null;
}
type InitialValues = {
  file: File;
};

const apiPersonRegisterCreate = (document: PersonRegisterUploadType) => {
  return customInstance<PersonRegisterUploadType>({
    data: document,
    headers: { 'Content-Type': 'multipart/form-data' },
    method: 'post',
    url: `/api/diploma/create/`,
  });
};

const useApiDocumentCreate = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof apiPersonRegisterCreate>>,
    TError,
    { data: PersonRegisterUploadType },
    TContext
  >;
}) => {
  const { mutation: mutationOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof apiPersonRegisterCreate>>,
    { data: PersonRegisterUploadType }
  > = (props) => {
    const { data } = props ?? {};

    return apiPersonRegisterCreate(data);
  };

  return useMutation<
    Awaited<ReturnType<typeof apiPersonRegisterCreate>>,
    TError,
    { data: PersonRegisterUploadType },
    TContext
  >(mutationFn, mutationOptions);
};

export const useUploadFreelancerPersonalRegisterDocument = (): {
  uploadFreelancerPersonRegisterDocument: (
    certificate: PersonRegisterUploadType,
  ) => Promise<AxiosResponse<PersonRegisterUploadType>>;
  docType: number | null;
  isLoading: boolean;
  error: null | Error;
  isSuccess: boolean;
  initialValues: InitialValues;
  resetUploadHook: () => void;
} => {
  const { mutateAsync, isLoading, error, isSuccess, reset } =
    useApiDocumentCreate({
      mutation: {
        mutationKey: 'upload-freelancer-PersonRegister-document',
        onSuccess: () => {
          const queriesToInvalidate = [
            'freelancer-documents',
            'freelancer-person-register-document',
            'freelancer-profile',
            'freelancer-details',
            'freelancer-signup-status',
          ];
          queriesToInvalidate.forEach((query) => {
            client.invalidateQueries(query);
          });
        },
      },
    });

  const uploadFreelancerPersonRegisterDocument = useCallback(
    async (freelancerDiplomaDocument: PersonRegisterUploadType) => {
      let response: AxiosResponse = {
        config: {},
        data: {},
        headers: {},
        request: {},
        status: 200,
        statusText: 'OK',
      };
      try {
        response = await mutateAsync({ data: freelancerDiplomaDocument });
        // we are expecting a 201 response (created if the doc is correctly created)
        if (response.status !== 201) throw new Error('Something went wrong');
      } catch (err) {
        console.log('Something went wrong');
      }
      return response;
    },
    [mutateAsync],
  );

  const { data } = useGetDocumentTypes();
  let personRegisterDocTypeId = null as unknown as number | null;
  if (data.length)
    personRegisterDocTypeId =
      data.find((dtype) => dtype.name === 'Inschrijving Personenregister')
        ?.id || null;

  const initialValues = {
    file: null as unknown as File,
  } as InitialValues;

  return {
    docType: personRegisterDocTypeId,
    error,
    initialValues,
    isLoading,
    isSuccess,
    resetUploadHook: reset,
    uploadFreelancerPersonRegisterDocument,
  };
};
